import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useLocale } from "../locale/locale-context.tsx";
import { useAuth } from "../auth/auth-context.tsx";
import { api, ApiError } from "../api/client.ts";
import { STREAM_EMBED_ORIGIN } from "../config/stream.ts";

// Minimal typing for the Cloudflare Stream embed SDK. The script attaches a
// global `Stream` factory that wraps an `<iframe>` and exposes a postMessage
// shim with `addEventListener("ended" | "timeupdate" | "play", ...)`. See:
//   https://developers.cloudflare.com/stream/viewing-videos/using-the-player-api/
interface StreamPlayer {
  addEventListener: (event: string, cb: () => void) => void;
  removeEventListener: (event: string, cb: () => void) => void;
  play: () => Promise<void>;
  currentTime: number;
  duration: number;
}
declare global {
  interface Window {
    Stream?: (iframe: HTMLIFrameElement) => StreamPlayer;
  }
}

const STREAM_SDK_URL = "https://embed.cloudflarestream.com/embed/sdk.latest.js";
let streamSdkPromise: Promise<void> | null = null;
function loadStreamSdk(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.Stream) return Promise.resolve();
  if (streamSdkPromise) return streamSdkPromise;
  streamSdkPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${STREAM_SDK_URL}"]`);
    const onLoad = () => resolve();
    if (existing) {
      if ((existing as HTMLScriptElement & { dataset: { loaded?: string } }).dataset.loaded) {
        resolve();
        return;
      }
      existing.addEventListener("load", onLoad, { once: true });
      existing.addEventListener("error", () => reject(new Error("stream-sdk")), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = STREAM_SDK_URL;
    s.async = true;
    s.addEventListener("load", () => {
      s.dataset.loaded = "1";
      resolve();
    });
    s.addEventListener("error", () => reject(new Error("stream-sdk")));
    document.head.appendChild(s);
  });
  return streamSdkPromise;
}

// Slug-keyed watch route — `/watch/:slug/:season/:episode`. Replaced the
// legacy id-keyed `WatchPage` in r5/A7.
interface EpisodeByPath {
  _id: string;
  seasonNumber: number;
  episodeNumber: number;
  titleEn: string;
  titleTh: string;
  durationSec: number;
  thumbnailUrl: string;
  streamVideoId: string;
  isFree: boolean;
  priceThb: number | null;
}

interface EpisodeListItem {
  _id: string;
  episodeNumber: number;
  seasonNumber: number;
  titleEn: string;
  titleTh: string;
  isFree: boolean;
}

interface WatchBySlugData {
  seriesId: string;
  slug: string;
  seriesTitleEn: string;
  seriesTitleTh: string;
  episode: EpisodeByPath;
}

interface RequiresUnlock {
  signedIn: boolean;
  loginUrl: string;
  packUrl: string;
  bundleUrl: string;
}

export function WatchBySlugPage() {
  const { slug = "", season = "1", episode = "1" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { locale } = useLocale();
  const [data, setData] = useState<WatchBySlugData | null>(null);
  const [episodes, setEpisodes] = useState<EpisodeListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [requiresUnlock, setRequiresUnlock] = useState<RequiresUnlock | null>(null);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [ended, setEnded] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const watchPath = `/watch/${slug}/${season}/${episode}`;
  const unitPrice = data?.episode.priceThb ?? 9;

  useEffect(() => {
    setData(null);
    setError(null);
    setRequiresUnlock(null);
    setEnded(false);
    setCountdown(5);
    void (async () => {
      try {
        const detail = await api.get<WatchBySlugData>(
          `/api/series/${encodeURIComponent(slug)}/episodes/${encodeURIComponent(season)}/${encodeURIComponent(episode)}`,
        );
        setData(detail);
        const list = await api.get<{ items: EpisodeListItem[] }>(
          `/api/series/${encodeURIComponent(slug)}/episodes`,
        );
        setEpisodes(list.items);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setError(locale === "th" ? "ไม่พบตอนนี้" : "Episode not found");
        } else if (err instanceof ApiError && err.status === 402) {
          // A1 endpoint returned 402 with requiresUnlock payload — render the
          // inline unlock screen instead of the player iframe.
          const body = (err.body ?? {}) as Partial<WatchBySlugData> & {
            requiresUnlock?: RequiresUnlock;
          };
          if (body.episode && body.seriesId) setData(body as WatchBySlugData);
          setRequiresUnlock(body.requiresUnlock ?? null);
        } else {
          setError((err as Error).message);
        }
      }
    })();
  }, [slug, season, episode, locale]);

  // Wallet balance for the inline unlock screen — refreshed when 402 hits.
  useEffect(() => {
    if (!user || !requiresUnlock) return;
    void (async () => {
      try {
        const w = await api.get<{ wallet: { balanceThb: number } }>("/api/wallet");
        setWalletBalance(w.wallet.balanceThb);
      } catch {
        setWalletBalance(0);
      }
    })();
  }, [user, requiresUnlock]);

  // Wallet-first unlock — debit, then re-fetch the now-paid episode.
  const unlockWithWallet = async (kind: "episode" | "bundle") => {
    setUnlockBusy(true);
    try {
      if (kind === "episode") {
        await api.post(`/api/wallet/unlock/episode`, {
          seriesSlug: slug,
          seasonNumber: Number(season),
          episodeNumber: Number(episode),
        });
      } else {
        await api.post(`/api/wallet/unlock/bundle`, { seriesSlug: slug });
      }
      setRequiresUnlock(null);
      setData(null);
      const detail = await api.get<WatchBySlugData>(
        `/api/series/${encodeURIComponent(slug)}/episodes/${encodeURIComponent(season)}/${encodeURIComponent(episode)}`,
      );
      setData(detail);
    } catch (err) {
      const msg = (err as Error).message;
      const shortMatch = /short.*?(\d+)/i.exec(msg);
      if (shortMatch) {
        navigate(`/account/wallet?need=${shortMatch[1]}&next=${encodeURIComponent(watchPath)}`);
        return;
      }
      setError(msg);
    } finally {
      setUnlockBusy(false);
    }
  };

  // Cloudflare Stream embed event wiring — load the SDK once and attach
  // listeners. When the episode either reports `ended` OR enters its last 10
  // seconds (Netflix-style "next up" early surface), we flip `ended=true` so
  // the up-next overlay slides in and the auto-advance countdown begins.
  useEffect(() => {
    if (!data || !iframeRef.current) return;
    let cancelled = false;
    let player: StreamPlayer | null = null;
    const NEAR_END_SECONDS = 10;
    const onEnded = () => {
      if (cancelled) return;
      setEnded(true);
    };
    const onTimeUpdate = () => {
      if (cancelled || !player) return;
      const dur = player.duration;
      const cur = player.currentTime;
      if (Number.isFinite(dur) && dur > 0 && dur - cur <= NEAR_END_SECONDS) {
        setEnded(true);
      }
    };
    void (async () => {
      try {
        await loadStreamSdk();
        if (cancelled || !iframeRef.current || !window.Stream) return;
        player = window.Stream(iframeRef.current);
        player.addEventListener("ended", onEnded);
        player.addEventListener("timeupdate", onTimeUpdate);
      } catch {
        // SDK didn't load (network/blocked). The next-up CTA is still visible
        // statically, so the user can click through manually.
      }
    })();
    return () => {
      cancelled = true;
      if (player) {
        try {
          player.removeEventListener("ended", onEnded);
          player.removeEventListener("timeupdate", onTimeUpdate);
        } catch {
          // Listener removal can throw if the iframe was already torn down;
          // safe to ignore on route change.
        }
      }
    };
  }, [data]);

  if (error) {
    return (
      <div className="flex min-h-[80vh] flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="font-heading text-2xl">{error}</p>
        <Link
          to={`/series/${slug}`}
          className="inline-flex h-11 items-center justify-center rounded-full bg-[var(--brand-ink)] px-6 text-sm font-semibold text-[var(--brand-paper)]"
        >
          {locale === "th" ? "กลับไปยังซีรีส์" : "Back to series"}
        </Link>
      </div>
    );
  }

  const seasonNum = Number(season);
  const episodeNum = Number(episode);
  const idx = episodes.findIndex(
    (e) => e.episodeNumber === episodeNum && e.seasonNumber === seasonNum,
  );
  const next = idx >= 0 ? episodes[idx + 1] : undefined;
  const nextHref =
    next && next.isFree
      ? `/watch/${slug}/${next.seasonNumber}/${next.episodeNumber}`
      : next
        ? `/series/${slug}`
        : `/series/${slug}`;

  // Once the episode ends, count down and auto-advance — Netflix-style. The
  // user can override with the explicit "Watch next" button (immediate) or
  // dismiss the overlay with "Stay here".
  useEffect(() => {
    if (!ended) return;
    if (countdown <= 0) {
      navigate(nextHref);
      return;
    }
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [ended, countdown, nextHref, navigate]);

  // Round-5 D8: drop the round-4 placeholder substitution helper now that
  // `tooling/scripts/upload-samples.ts` writes a real per-slug Stream UID
  // into every episode's `streamVideoId` through `seed-content.ts`.
  const resolvedVideoId = data?.episode.streamVideoId ?? "";

  return (
    <div className="relative bg-black text-white">
      <div className="mx-auto flex max-w-md flex-col gap-3 px-4 pb-8 pt-4 sm:max-w-lg">
        <Link
          to={`/series/${slug}`}
          className="text-xs font-medium text-white/80 transition-opacity hover:opacity-100"
        >
          {locale === "th" ? "← กลับไปยังซีรีส์" : "← Back to series"}
        </Link>

        {data && requiresUnlock ? (
          <UnlockInlineCard
            seriesTitleEn={data.seriesTitleEn}
            seriesTitleTh={data.seriesTitleTh}
            episodeTitleEn={data.episode.titleEn}
            episodeTitleTh={data.episode.titleTh}
            seasonNumber={data.episode.seasonNumber}
            episodeNumber={data.episode.episodeNumber}
            thumbnailUrl={data.episode.thumbnailUrl}
            unitPriceThb={unitPrice}
            walletBalanceThb={walletBalance}
            user={user}
            unlockBusy={unlockBusy}
            locale={locale}
            loginUrl={requiresUnlock.loginUrl}
            onUnlock={(kind) => unlockWithWallet(kind)}
          />
        ) : data ? (
          <>
            <div className="relative aspect-poster w-full overflow-hidden rounded-2xl bg-black">
              <iframe
                ref={iframeRef}
                title={locale === "th" ? data.episode.titleTh : data.episode.titleEn}
                // No `autoplay=true` here: browsers block autoplay-with-sound
                // without a user gesture, so an `autoplay=true&muted=false`
                // URL gets silently force-muted by Chrome/Safari and the user
                // has to click the speaker. Dropping autoplay means the user
                // taps play — that gesture allows audio, so the episode opens
                // with sound on, which is what the user asked for.
                src={`${STREAM_EMBED_ORIGIN}/${resolvedVideoId}?preload=auto&poster=${encodeURIComponent(data.episode.thumbnailUrl)}`}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
                className="h-full w-full border-0"
              />

              {/* Netflix-style up-next overlay. Slides in once the embed
                  reports "ended" OR enters the last 10 seconds; the user can
                  click "Watch next" now or let the 5s countdown auto-advance. */}
              {ended && next ? (
                <div className="absolute inset-0 flex flex-col items-center justify-end bg-black/70 p-5">
                  <div className="w-full space-y-3 rounded-2xl bg-black/70 p-4 backdrop-blur">
                    <p className="text-xs font-medium uppercase tracking-wide text-white/70">
                      {locale === "th" ? "ตอนต่อไปใน" : "Up next in"} {countdown}s
                    </p>
                    <p className="line-clamp-2 text-base font-bold leading-tight">
                      EP{next.episodeNumber} · {locale === "th" ? next.titleTh : next.titleEn}
                    </p>
                    {next.isFree ? (
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={`/watch/${slug}/${next.seasonNumber}/${next.episodeNumber}`}
                          className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-white px-5 text-sm font-bold text-black"
                        >
                          {locale === "th" ? "ดูตอนต่อไป →" : "Watch next →"}
                        </Link>
                        <button
                          type="button"
                          onClick={() => {
                            setEnded(false);
                            setCountdown(5);
                          }}
                          className="inline-flex h-10 items-center justify-center rounded-full border border-white/40 px-5 text-sm font-medium text-white hover:bg-white/10"
                        >
                          {locale === "th" ? "อยู่หน้านี้" : "Stay here"}
                        </button>
                      </div>
                    ) : (
                      <NextEpisodeUnlockCTA
                        slug={slug}
                        nextSeason={next.seasonNumber}
                        nextEpisode={next.episodeNumber}
                        unitPriceThb={unitPrice}
                        walletBalanceThb={walletBalance}
                        user={user}
                        locale={locale}
                        watchPath={watchPath}
                        navigate={navigate}
                        variant="overlay"
                      />
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            {/* Persistent floating "Next episode" pill — always visible while
                watching so the demo audience can skip without waiting for the
                end-of-video timer. Sits below the player at md+ and floats
                bottom-right on mobile so a thumb can reach it. */}
            {next ? (
              <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center sm:bottom-6">
                {next.isFree ? (
                  <Link
                    to={`/watch/${slug}/${next.seasonNumber}/${next.episodeNumber}`}
                    className="pointer-events-auto inline-flex h-11 items-center gap-2 rounded-full bg-white/95 px-5 text-sm font-bold text-black shadow-2xl backdrop-blur transition-transform hover:-translate-y-0.5"
                  >
                    {locale === "th"
                      ? `ดูตอนต่อไป · EP${next.episodeNumber} →`
                      : `Next episode · EP${next.episodeNumber} →`}
                  </Link>
                ) : (
                  <NextEpisodeUnlockCTA
                    slug={slug}
                    nextSeason={next.seasonNumber}
                    nextEpisode={next.episodeNumber}
                    unitPriceThb={unitPrice}
                    walletBalanceThb={walletBalance}
                    user={user}
                    locale={locale}
                    watchPath={watchPath}
                    navigate={navigate}
                    variant="floating"
                  />
                )}
              </div>
            ) : null}

            <div className="space-y-1">
              <p className="font-mono text-xs text-white/70">
                S{data.episode.seasonNumber} · EP{data.episode.episodeNumber}
              </p>
              <h1 className="font-heading text-lg font-bold">
                {locale === "th" ? data.episode.titleTh : data.episode.titleEn}
              </h1>
              <p className="text-xs text-white/70">
                {locale === "th" ? data.seriesTitleTh : data.seriesTitleEn}
              </p>
            </div>

            {next ? (
              <div className="mt-2 rounded-2xl border border-white/15 bg-white/5 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-white/60">
                  {locale === "th" ? "ตอนต่อไป" : "Up next"}
                </p>
                <p className="mt-1 truncate text-sm font-semibold text-white">
                  EP{next.episodeNumber} · {locale === "th" ? next.titleTh : next.titleEn}
                </p>
                {next.isFree ? (
                  <Link
                    to={`/watch/${slug}/${next.seasonNumber}/${next.episodeNumber}`}
                    className="mt-3 inline-flex h-10 items-center justify-center rounded-full bg-white px-5 text-sm font-semibold text-black"
                  >
                    {locale === "th" ? "เล่นตอนถัดไป" : "Play next"}
                  </Link>
                ) : (
                  <NextEpisodeUnlockCTA
                    slug={slug}
                    nextSeason={next.seasonNumber}
                    nextEpisode={next.episodeNumber}
                    unitPriceThb={unitPrice}
                    walletBalanceThb={walletBalance}
                    user={user}
                    locale={locale}
                    watchPath={watchPath}
                    navigate={navigate}
                    variant="inline"
                  />
                )}
              </div>
            ) : null}
          </>
        ) : (
          <div className="aspect-poster flex items-center justify-center rounded-2xl bg-white/5 text-sm text-white/60">
            {locale === "th" ? "กำลังโหลด…" : "Loading…"}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline unlock card — rendered when GET /api/series/.../episodes/:s/:e returns
// 402. Wallet-primary, same shape as the unlock sheet on /series/:slug, but
// here it replaces the iframe entirely so the user can pay-then-play without
// bouncing back to the detail page.
// ---------------------------------------------------------------------------

interface UnlockInlineCardProps {
  seriesTitleEn: string;
  seriesTitleTh: string;
  episodeTitleEn: string;
  episodeTitleTh: string;
  seasonNumber: number;
  episodeNumber: number;
  thumbnailUrl: string;
  unitPriceThb: number;
  walletBalanceThb: number | null;
  user: { email: string } | null;
  unlockBusy: boolean;
  locale: "th" | "en";
  loginUrl: string;
  onUnlock: (kind: "episode" | "bundle") => void;
}

function UnlockInlineCard(p: UnlockInlineCardProps) {
  const canCover = !!p.user && p.walletBalanceThb != null && p.walletBalanceThb >= p.unitPriceThb;
  const short = Math.max(0, p.unitPriceThb - (p.walletBalanceThb ?? 0));
  return (
    <div className="relative aspect-poster w-full overflow-hidden rounded-2xl bg-black">
      <img
        src={p.thumbnailUrl}
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-40 blur-sm"
      />
      <div className="absolute inset-0 flex flex-col items-center justify-end gap-3 bg-gradient-to-t from-black/95 via-black/60 to-black/20 p-6 text-center">
        <p className="font-mono text-xs uppercase tracking-wider text-white/70">
          S{p.seasonNumber} · EP{p.episodeNumber}
        </p>
        <p className="line-clamp-2 font-heading text-base font-bold leading-tight text-white">
          {p.locale === "th" ? p.episodeTitleTh : p.episodeTitleEn}
        </p>
        <p className="text-xs text-white/70">
          {p.locale === "th" ? p.seriesTitleTh : p.seriesTitleEn}
        </p>

        {!p.user ? (
          <Link
            to={p.loginUrl}
            className="mt-1 inline-flex h-11 items-center justify-center rounded-full bg-[var(--brand-rose)] px-6 text-sm font-bold text-white shadow-lg"
          >
            {p.locale === "th"
              ? `เข้าสู่ระบบเพื่อปลดล็อก ฿${p.unitPriceThb}`
              : `Sign in to unlock — ฿${p.unitPriceThb}`}
          </Link>
        ) : (
          <button
            type="button"
            disabled={p.unlockBusy || p.walletBalanceThb === null}
            onClick={() => p.onUnlock("episode")}
            className="mt-1 inline-flex h-11 items-center justify-center rounded-full bg-[var(--brand-rose)] px-6 text-sm font-bold text-white shadow-lg disabled:opacity-50"
          >
            {p.unlockBusy
              ? "…"
              : canCover
                ? p.locale === "th"
                  ? `ใช้กระเป๋าเงิน · ฿${p.unitPriceThb} (คงเหลือ ฿${p.walletBalanceThb})`
                  : `Use wallet · ฿${p.unitPriceThb} (balance ฿${p.walletBalanceThb})`
                : p.locale === "th"
                  ? `เติม ฿${short} แล้วปลดล็อก ฿${p.unitPriceThb}`
                  : `Top up ฿${short} & unlock ฿${p.unitPriceThb}`}
          </button>
        )}
        {p.user ? (
          <button
            type="button"
            disabled={p.unlockBusy}
            onClick={() => p.onUnlock("bundle")}
            className="text-xs font-medium text-white/80 underline-offset-4 hover:text-white hover:underline"
          >
            {p.locale === "th" ? "เหมาทั้งซีรีส์จากกระเป๋าเงิน" : "Unlock whole series from wallet"}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Locked next-episode CTA — replaces the old "go to /series/:slug" link with
// a wallet-first unlock button. Three layout variants so we can drop it into
// the existing overlay / floating-pill / up-next-card without restyling each
// site.
// ---------------------------------------------------------------------------

interface NextEpisodeUnlockCTAProps {
  slug: string;
  nextSeason: number;
  nextEpisode: number;
  unitPriceThb: number;
  walletBalanceThb: number | null;
  user: { email: string } | null;
  locale: "th" | "en";
  watchPath: string;
  navigate: (to: string) => void;
  variant: "overlay" | "floating" | "inline";
}

function NextEpisodeUnlockCTA(p: NextEpisodeUnlockCTAProps) {
  const [busy, setBusy] = useState(false);
  const targetWatchPath = `/watch/${p.slug}/${p.nextSeason}/${p.nextEpisode}`;

  const onClick = async () => {
    if (!p.user) {
      p.navigate(`/login?next=${encodeURIComponent(targetWatchPath)}`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/wallet/unlock/episode`, {
        seriesSlug: p.slug,
        seasonNumber: p.nextSeason,
        episodeNumber: p.nextEpisode,
      });
      p.navigate(targetWatchPath);
    } catch (err) {
      const msg = (err as Error).message;
      const shortMatch = /short.*?(\d+)/i.exec(msg);
      if (shortMatch) {
        p.navigate(
          `/account/wallet?need=${shortMatch[1]}&next=${encodeURIComponent(targetWatchPath)}`,
        );
        return;
      }
      // Anything else — fall back to the detail-page unlock sheet so the
      // user has a way out without seeing a raw error string.
      p.navigate(`/series/${p.slug}`);
    } finally {
      setBusy(false);
    }
  };

  const label = busy
    ? "…"
    : p.locale === "th"
      ? `ปลดล็อกตอนต่อไป · ฿${p.unitPriceThb}`
      : `Unlock next · ฿${p.unitPriceThb}`;

  const classes = {
    overlay:
      "inline-flex h-10 items-center justify-center rounded-full bg-[var(--brand-rose)] px-5 text-sm font-bold text-white disabled:opacity-50",
    floating:
      "pointer-events-auto inline-flex h-11 items-center gap-2 rounded-full bg-[var(--brand-rose)] px-5 text-sm font-bold text-white shadow-2xl backdrop-blur transition-transform hover:-translate-y-0.5 disabled:opacity-50",
    inline:
      "mt-3 inline-flex h-10 items-center justify-center rounded-full bg-[var(--brand-rose)] px-5 text-sm font-semibold text-white disabled:opacity-50",
  } as const;

  return (
    <button type="button" onClick={onClick} disabled={busy} className={classes[p.variant]}>
      {label}
    </button>
  );
}
